import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { SignInPage } from './auth/SignInPage';
import { InviteAcceptPage } from './auth/InviteAcceptPage';

// Route-level code splitting (frontend-performance-critic, swarm review):
// Sign-in is the near-universal landing screen for a rep who hasn't
// authenticated yet, so it's loaded eagerly; every other screen is a
// candidate reps only reach AFTER signing in, so none of its code should be
// on the wire before that. Mirrors the CRM's own `src/router.tsx` pattern.
const MyCampaignsPage = lazy(() =>
  import('./campaigns/MyCampaignsPage').then((m) => ({ default: m.MyCampaignsPage })),
);
const DealFormPage = lazy(() =>
  import('./deals/DealFormPage').then((m) => ({ default: m.DealFormPage })),
);
const MyDealsListPage = lazy(() =>
  import('./deals/MyDealsListPage').then((m) => ({ default: m.MyDealsListPage })),
);
const MyDealsDetailPage = lazy(() =>
  import('./deals/MyDealsDetailPage').then((m) => ({ default: m.MyDealsDetailPage })),
);

// Staff screens (PP-9.6) — lazy-loaded on their own boundary, same reasoning
// as every rep screen above: a partner rep never needs any of this code, so
// it must never grow the bundle they actually download (frontend-
// performance-critic, swarm review).
const StaffHome = lazy(() =>
  import('./staff/StaffHomePage').then((m) => ({ default: m.StaffHomePage })),
);
const StaffRegistrationDetail = lazy(() =>
  import('./staff/StaffRegistrationDetailPage').then((m) => ({
    default: m.StaffRegistrationDetailPage,
  })),
);
const StaffInviteRep = lazy(() =>
  import('./staff/InviteRepPage').then((m) => ({ default: m.InviteRepPage })),
);

// A fixed-height fallback -- avoids a CLS regression once the real page
// pops in (frontend-performance-critic, swarm review: fixing LCP on
// Sign-in must not introduce a layout shift on every other screen).
const LazyFallback = (): JSX.Element => (
  <Box sx={{ display: 'flex', minHeight: '60vh', alignItems: 'center', justifyContent: 'center' }}>
    <CircularProgress aria-label="Loading page" />
  </Box>
);

export const PortalRoutes = (): JSX.Element => (
  <Suspense fallback={<LazyFallback />}>
    <Routes>
      <Route path="/sign-in" element={<SignInPage />} />
      <Route path="/invite-accept" element={<InviteAcceptPage />} />
      <Route index element={<Navigate to="/campaigns" replace />} />
      <Route path="/campaigns" element={<MyCampaignsPage />} />
      <Route path="/deals/new" element={<DealFormPage />} />
      <Route path="/deals" element={<MyDealsListPage />} />
      <Route path="/deals/:regId" element={<MyDealsDetailPage />} />
      {/* Staff screens (PP-9.6) -- a staff member lands on /staff directly
          via the BFF's /auth/staff/callback redirect, never through the
          rep-facing routes above. */}
      <Route path="/staff" element={<StaffHome />} />
      <Route path="/staff/registrations/:regId" element={<StaffRegistrationDetail />} />
      <Route path="/staff/invite-rep" element={<StaffInviteRep />} />
      <Route path="*" element={<Navigate to="/campaigns" replace />} />
    </Routes>
  </Suspense>
);
